"use client";

import React, { useState, useEffect } from "react";
import {
  Send,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Phone,
  Mail,
  MessageSquare,
  X,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { campaignsApi, CampaignMember } from "@/services/campaignsApi";
import { useReplyProbabilityMap } from "@/components/communications/ml";
import { PredictionConfidenceBar } from "@/components/ml";

interface CampaignMemberSendProps {
  campaignId: string;
  onClose: () => void;
}

type SendStatus = "pending" | "sent" | "failed";
type SortField = "name" | "status";
type SortDir = "asc" | "desc";
type Channel = "sms" | "email" | "whatsapp" | "rcs";

interface MemberRow extends CampaignMember {
  localStatus: SendStatus;
  sending: boolean;
  errorMsg?: string;
}

export const CampaignMemberSend: React.FC<CampaignMemberSendProps> = ({
  campaignId,
  onClose,
}) => {
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contentTemplate, setContentTemplate] = useState("");
  const [templateExpanded, setTemplateExpanded] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<Channel>("sms");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [sendingAll, setSendingAll] = useState(false);

  // Fetch members on mount
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        const data = await campaignsApi.getCampaignMembers(campaignId);
        setContentTemplate(data.content_template || "");
        if (data.channels?.length) {
          const ch = data.channels[0] as Channel;
          setSelectedChannel(["sms", "email", "whatsapp", "rcs"].includes(ch) ? ch : "sms");
        }
        setMembers(
          data.members.map((m) => ({
            ...m,
            localStatus: (m.send_status === "sent" || m.send_status === "failed"
              ? m.send_status
              : "pending") as SendStatus,
            sending: false,
          }))
        );
      } catch (err: any) {
        setError(err?.message || "Failed to load campaign members");
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, [campaignId]);

  // Counts
  const sentCount = members.filter((m) => m.localStatus === "sent").length;
  const pendingCount = members.filter((m) => m.localStatus === "pending").length;
  const failedCount = members.filter((m) => m.localStatus === "failed").length;

  // Phase 5c — batch-score every member for predicted reply probability.
  // Hook handles 503/model_not_trained gracefully (notTrained flag); we show
  // a — for those rows instead of hiding the column.
  const memberIds = React.useMemo(
    () => members.map((m) => m.member_id).filter((id): id is number => typeof id === "number" && id > 0),
    [members],
  );
  const replyProb = useReplyProbabilityMap(memberIds);

  // Filter and sort
  const filtered = members.filter((m) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.first_name.toLowerCase().includes(q) ||
      m.last_name.toLowerCase().includes(q) ||
      `${m.first_name} ${m.last_name}`.toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortField === "name") {
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
      return nameA.localeCompare(nameB) * dir;
    }
    // Sort by status: pending < sent < failed
    const order: Record<SendStatus, number> = { pending: 0, sent: 1, failed: 2 };
    return (order[a.localStatus] - order[b.localStatus]) * dir;
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  // Send to a single member
  const sendToMember = async (memberId: number) => {
    setMembers((prev) =>
      prev.map((m) =>
        m.member_id === memberId ? { ...m, sending: true, errorMsg: undefined } : m
      )
    );
    try {
      await campaignsApi.sendIndividualMessage(campaignId, memberId, selectedChannel);
      setMembers((prev) =>
        prev.map((m) =>
          m.member_id === memberId
            ? { ...m, sending: false, localStatus: "sent", messages_sent: m.messages_sent + 1 }
            : m
        )
      );
    } catch (err: any) {
      setMembers((prev) =>
        prev.map((m) =>
          m.member_id === memberId
            ? { ...m, sending: false, localStatus: "failed", errorMsg: err?.message || "Send failed" }
            : m
        )
      );
    }
  };

  // Send all pending members sequentially
  const sendAll = async () => {
    setSendingAll(true);
    const pendingMembers = members.filter((m) => m.localStatus === "pending");
    for (const member of pendingMembers) {
      await sendToMember(member.member_id);
      // 500ms delay between sends
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    setSendingAll(false);
  };

  // Status badge renderer
  const StatusBadge = ({ status, errorMsg }: { status: SendStatus; errorMsg?: string }) => {
    if (status === "sent") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" /> Sent
        </span>
      );
    }
    if (status === "failed") {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"
          title={errorMsg || "Failed"}
        >
          <XCircle className="w-3 h-3" /> Failed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        <Clock className="w-3 h-3" /> Pending
      </span>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 w-full">
        <div className="flex items-center justify-center gap-2 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading campaign members...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 w-full">
        <div className="flex items-center justify-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
        <div className="mt-4 text-center">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700 underline">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg w-full flex flex-col max-h-[90vh]">
      {/* Header */}
      <div className="border-b px-6 py-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Send to Members</h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-paper-2 text-ink-500">
              {members.length} members
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content template preview */}
        {contentTemplate && (
          <div className="bg-gray-50 rounded-md border">
            <button
              onClick={() => setTemplateExpanded(!templateExpanded)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              <span className="font-medium">Content Template</span>
              <span className="text-xs">{templateExpanded ? "Hide" : "Show"}</span>
            </button>
            {templateExpanded && (
              <div className="px-3 pb-3 text-sm text-gray-700 whitespace-pre-wrap border-t pt-2">
                {contentTemplate}
              </div>
            )}
          </div>
        )}

        {/* Channel selector + Send All + Search */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            {/* Channel buttons */}
            <div className="flex rounded-md border overflow-hidden">
              <button
                onClick={() => setSelectedChannel("sms")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
                  selectedChannel === "sms"
                    ? "bg-v2accent text-white"
                    : "bg-surface text-ink-500 hover:text-ink"
                }`}
              >
                <Phone className="w-3.5 h-3.5" /> SMS
              </button>
              <button
                onClick={() => setSelectedChannel("whatsapp")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors border-l ${
                  selectedChannel === "whatsapp"
                    ? "bg-v2accent text-white"
                    : "bg-surface text-ink-500 hover:text-ink"
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5" /> WhatsApp
              </button>
              <button
                onClick={() => setSelectedChannel("email")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors border-l ${
                  selectedChannel === "email"
                    ? "bg-v2accent text-white"
                    : "bg-surface text-ink-500 hover:text-ink"
                }`}
              >
                <Mail className="w-3.5 h-3.5" /> Email
              </button>
              <button
                onClick={() => setSelectedChannel("rcs")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors border-l ${
                  selectedChannel === "rcs"
                    ? "bg-v2accent text-white"
                    : "bg-surface text-ink-500 hover:text-ink"
                }`}
              >
                <Phone className="w-3.5 h-3.5" /> RCS
              </button>
            </div>

            {/* Send All button */}
            <button
              onClick={sendAll}
              disabled={sendingAll || pendingCount === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-v2accent text-white hover:bg-v2accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sendingAll ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <Send className="w-3.5 h-3.5" /> Send All ({pendingCount})
                </>
              )}
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-56"
            />
          </div>
        </div>

        {/* Count indicators */}
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-green-500" /> {sentCount} sent
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-gray-400" /> {pendingCount} pending
          </span>
          <span className="flex items-center gap-1">
            <XCircle className="w-3 h-3 text-red-500" /> {failedCount} failed
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th
                className="text-left px-6 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-gray-900"
                onClick={() => toggleSort("name")}
              >
                Name {sortField === "name" && (sortDir === "asc" ? "\u2191" : "\u2193")}
              </th>
              <th className="text-left px-6 py-3 font-medium text-gray-600">Phone</th>
              <th className="text-left px-6 py-3 font-medium text-gray-600">Email</th>
              <th
                className="text-left px-6 py-3 font-medium text-gray-600"
                title={replyProb.notTrained ? 'Reply-rate model not yet deployed' : 'Predicted reply propensity (ML)'}
              >
                Reply propensity
              </th>
              <th
                className="text-left px-6 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-gray-900"
                onClick={() => toggleSort("status")}
              >
                Status {sortField === "status" && (sortDir === "asc" ? "\u2191" : "\u2193")}
              </th>
              <th className="text-left px-6 py-3 font-medium text-gray-600">Channel</th>
              <th className="text-center px-6 py-3 font-medium text-gray-600">Messages Sent</th>
              <th className="text-right px-6 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-gray-400">
                  {searchQuery ? "No members match your search." : "No members found."}
                </td>
              </tr>
            ) : (
              sorted.map((member) => (
                <tr key={member.member_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-3 font-medium text-gray-900">
                    {member.first_name} {member.last_name}
                  </td>
                  <td className="px-6 py-3 text-gray-600">{member.phone || "\u2014"}</td>
                  <td className="px-6 py-3 text-gray-600">{member.email || "\u2014"}</td>
                  <td className="px-6 py-3">
                    {(() => {
                      const p = replyProb.byId[member.member_id];
                      if (replyProb.notTrained) return <span className="text-xs text-gray-400">model not deployed</span>;
                      if (replyProb.isLoading) return <span className="text-xs text-gray-400">\u2026</span>;
                      if (p == null) return <span className="text-xs text-gray-400">\u2014</span>;
                      return <PredictionConfidenceBar probability={p} variant="compact" />;
                    })()}
                  </td>
                  <td className="px-6 py-3">
                    <StatusBadge status={member.localStatus} errorMsg={member.errorMsg} />
                  </td>
                  <td className="px-6 py-3">
                    {member.channel_attempts && Object.keys(member.channel_attempts).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(member.channel_attempts).map(([ch, st]) => (
                          <span
                            key={ch}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                              st === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}
                            title={`${ch}: ${st}`}
                          >
                            {ch}
                            {st === 'sent' ? (
                              <CheckCircle className="w-2.5 h-2.5" />
                            ) : (
                              <XCircle className="w-2.5 h-2.5" />
                            )}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {member.preferred_channel || selectedChannel}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-center text-gray-600">{member.messages_sent}</td>
                  <td className="px-6 py-3 text-right">
                    {member.localStatus === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-sm font-medium">
                        <CheckCircle className="w-4 h-4" /> Sent
                      </span>
                    ) : (
                      <button
                        onClick={() => sendToMember(member.member_id)}
                        disabled={member.sending || sendingAll}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-v2accent text-white hover:bg-v2accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {member.sending ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending
                          </>
                        ) : (
                          <>
                            <Send className="w-3.5 h-3.5" /> Send
                          </>
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CampaignMemberSend;
